import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../Store/hooks';
import { generateSummary } from '../../Store/StudentSlices/getRecordingsSlice';
import type { Recording } from './types';

type RecordingTranscriptProps = {
  recording: Recording;
};

const RecordingTranscript = ({ recording }: RecordingTranscriptProps) => {
  const dispatch = useAppDispatch();
  const { isGeneratingSummary } = useAppSelector((state) => state.getRecordings);
  const [activeTab, setActiveTab] = useState<'Transcript' | 'Summary'>('Transcript');
  const [searchQuery, setSearchQuery] = useState('');

  // Debugging prop changes
  console.log(`📝 [RECORDING TRANSCRIPT] Render: activeTab=${activeTab}, summaryLength=${recording.summary?.length || 0}`);

  const handleTabClick = (tab: 'Transcript' | 'Summary') => {
    console.log('📑 [TRANSCRIPT TAB] User clicked tab:', tab);
    setActiveTab(tab);
    
    if (tab === 'Summary') {
      console.log('🚀 [TRANSCRIPT TAB] Triggering summary generation for:', recording.id);
      dispatch(generateSummary(recording.id));
    }
  };

  const filteredSegments = recording.transcript.segments.filter((segment) =>
    segment.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
    segment.participant_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
      {/* Tabs */}
      <div className="flex items-center justify-between border-b border-slate-100 px-4 pt-4">
        <div className="flex items-center gap-6">
          {(['Transcript', 'Summary'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabClick(tab)}
              className={`pb-3 text-sm font-semibold transition-all relative ${
                activeTab === tab
                  ? 'text-indigo-600'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <div className="flex items-center gap-2">
                {tab === 'Transcript' && (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                )}
                
                {tab === 'Summary' && (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                {tab}
              </div>
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-full"></div>
              )}
            </button>
          ))}
        </div>
        <button className="flex items-center gap-2 text-slate-400 hover:text-slate-600 pb-3">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          <span className="text-xs font-semibold">Export</span>
        </button>
      </div>

      <div className="flex flex-col flex-1 p-4 overflow-hidden">
        {activeTab === 'Transcript' && (
          <>
            <div className="relative mb-6">
              <svg 
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search transcript"
                className="w-full bg-slate-50 border-none rounded-xl py-2.5 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-indigo-100 transition-all outline-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
              <div className="flex flex-col gap-6">
                {filteredSegments.length > 0 ? (
                  filteredSegments.map((segment, index) => (
                    <div key={index} className="flex gap-4 group">
                      <div className={`h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-white ${
                        segment.participant_id.includes('sarah') ? 'bg-indigo-400' : 'bg-amber-400'
                      }`}>
                        {getInitials(segment.participant_name)}
                      </div>
                      <div className="flex flex-col gap-1.5 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-900">{segment.participant_name}</span>
                          <span className="text-[10px] font-medium text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded">
                            {segment.timestamp.split(':').slice(0, 2).join(':')}
                          </span>
                        </div>
                        <p className="text-sm leading-relaxed text-slate-600 group-hover:text-slate-900 transition-colors">
                          {segment.text}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400 italic">
                    <p>No matches found</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {activeTab === 'Summary' && (
          <div className="flex flex-col gap-4 text-slate-600 h-full overflow-y-auto pr-2 custom-scrollbar">
             <div className="flex items-center justify-between">
               <h4 className="text-sm font-bold text-slate-900">Session Summary</h4>
               {isGeneratingSummary && (
                 <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600"></div>
               )}
             </div>
             {isGeneratingSummary ? (
               <div className="flex flex-col items-center justify-center h-full py-20 text-slate-400 gap-4">
                 <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-100 border-t-indigo-600"></div>
                 <div className="flex flex-col items-center gap-1">
                   <p className="text-sm font-bold text-slate-900">AI is working</p>
                   <p className="text-xs text-slate-500 italic">Generating a structured overview from the transcript...</p>
                 </div>
               </div>
             ) : recording.summary ? (
               <div className="flex flex-col gap-6">
                 <p className="text-[15px] leading-relaxed text-slate-600 whitespace-pre-line">{recording.summary}</p>
                 
                 <div className="pt-6 border-t border-slate-50">
                    <button 
                      onClick={() => {
                        console.log('🖱️ [TRANSCRIPT TAB] Regeneration requested');
                        dispatch(generateSummary(recording.id));
                      }}
                      className="text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors flex items-center gap-2"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Regenerate Summary
                    </button>
                 </div>
               </div>
             ) : (
               <div className="flex flex-col items-center justify-center h-full py-20 text-center px-8">
                 <h3 className="text-2xl font-serif text-slate-900 mb-4">Session Summary</h3>
                 <p className="text-slate-500 text-[15px] leading-relaxed max-w-md mb-8">
                   Pulls from the transcript to create a structured overview — executive snapshot, objectives, themes, and agreed next steps.
                 </p>
                 <button 
                  onClick={() => {
                    console.log('🖱️ [TRANSCRIPT TAB] Generate button clicked');
                    dispatch(generateSummary(recording.id));
                  }}
                  className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#6366f1] to-[#a855f7] text-white text-sm font-bold rounded-xl hover:opacity-90 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
                 >
                   Generate Summary
                   <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-6.857 2.286L12 21l-2.286-6.857L3 12l6.857-2.286L12 3z" />
                   </svg>
                 </button>
               </div>
             )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordingTranscript;
